/**
 * The values panel's second tab — download the template, fill it in a spreadsheet, send it
 * back (§D3 manual submissions).
 *
 * The grid is the better path for a dozen units; this one exists because a reporting round
 * is often run in a spreadsheet already, by someone who is not the person with the portal
 * open. So the two tabs are the *same* submission by two routes: the template's header is
 * the header `toCsv` writes, and the file dropped here is posted with the same
 * `kind_hint` and the same four pieces of metadata as the grid's own submit.
 *
 * **The template is a link, not a fetch.** `indicatorTemplateUrl` names the four selects in
 * its query string, the server names the file in its `Content-Disposition`, and the
 * browser's own download is what "download the template" means to everyone who has ever
 * used one.
 *
 * **Only `.csv` is accepted, and the refusal is immediate.** An `.xlsx` would be refused by
 * the server after the whole file has been transferred, which for a reporter on a bad link
 * is minutes spent to be told the obvious; the extension is checked before a byte moves.
 */
import { useCallback, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Btn } from '../../../components/Btn';
import { indicatorTemplateUrl, type SubmissionLevel } from '../../../lib/api';
import { Icon } from '../../../lib/icons';
import { CSV_EXTENSION, isCsvFile } from './values';

export interface ValuesCsvProps {
  indicatorId: string;
  level: SubmissionLevel;
  period: number;
  country: string | null;
  /** Whether a file may be picked at all — the submitter block gates this like the grid. */
  enabled: boolean;
  onPick: (file: File) => void;
}

export function ValuesCsv({
  indicatorId,
  level,
  period,
  country,
  enabled,
  onPick,
}: ValuesCsvProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  const take = useCallback(
    (file: File | null | undefined) => {
      if (!file) return;
      if (!isCsvFile(file.name)) {
        setRefused(file.name);
        return;
      }
      setRefused(null);
      onPick(file);
    },
    [onPick],
  );

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setOver(false);
      take(e.dataTransfer?.files?.[0]);
    },
    [take],
  );

  return (
    <div className="mgr-iv-csv" data-testid="mgr-iv-csv">
      <p className="mgr-hint">{t('manager.values.csv.lead')}</p>
      <p>
        <a
          className="btn"
          data-testid="mgr-iv-template"
          href={indicatorTemplateUrl({ indicator: indicatorId, level, period, country })}
          download
        >
          <Icon name="download" size={14} />
          {t('manager.values.csv.template')}
        </a>
      </p>

      <div
        className={`mgr-drop${over ? ' over' : ''}${enabled ? '' : ' off'}`}
        data-testid="mgr-iv-drop"
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={enabled ? onDrop : (e) => e.preventDefault()}
      >
        <Icon name="upload" size={28} />
        <p className="mgr-drop-title">{t('manager.values.csv.dropTitle')}</p>
        <Btn variant="primary" disabled={!enabled} onClick={() => inputRef.current?.click()}>
          {t('manager.drop.browse')}
        </Btn>
        {enabled ? null : (
          <p className="mgr-drop-cap" data-testid="mgr-iv-drop-needs-submitter">
            {t('manager.submitter.needed')}
          </p>
        )}
        <p className="mgr-drop-formats">{t('manager.values.csv.formats')}</p>
        <input
          ref={inputRef}
          type="file"
          className="mgr-file-input"
          data-testid="mgr-iv-file-input"
          aria-label={t('manager.values.csv.inputLabel')}
          accept={CSV_EXTENSION}
          onChange={(e) => {
            take(e.target.files?.[0]);
            // Clearing lets the same file be picked again after a refusal.
            e.target.value = '';
          }}
        />
      </div>

      {refused ? (
        <p className="mgr-error" role="alert" data-testid="mgr-iv-drop-error">
          {t('manager.values.csv.notCsv', { name: refused })}
        </p>
      ) : null}
    </div>
  );
}

export default ValuesCsv;
